import React, { useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  toggleJob,
  selectAll,
  clearScope,
  toggleDropdown,
  openDropdown,
  closeDropdown,
  setScopeSearch,
  fetchJobsForScope,
  selectScopedJobIds,
  selectScopeDropdownOpen,
  selectScopeJobs,
  selectScopeJobsLoading,
  selectScopeJobsLoaded,
  selectScopeSearch,
} from "../../redux/slices/job-scope-slice";

const JOB_COLORS = ["#7B5AC4", "#10B981", "#8B5CF6", "#F59E0B", "#06B6D4", "#EC4899"];
export const jobColor = (id) =>
  JOB_COLORS[Math.abs(Number(id) || 0) % JOB_COLORS.length];

export default function JobScopeSelector() {
  const dispatch = useDispatch();
  const scope = useSelector(selectScopedJobIds);
  const open = useSelector(selectScopeDropdownOpen);
  const jobs = useSelector(selectScopeJobs);
  const loading = useSelector(selectScopeJobsLoading);
  const loaded = useSelector(selectScopeJobsLoaded);
  const search = useSelector(selectScopeSearch);
  const ref = useRef();
  const searchRef = useRef();

  // Fetch once on mount (the slice caches across pages)
  useEffect(() => {
    if (!loaded && !loading) dispatch(fetchJobsForScope());
  }, [dispatch, loaded, loading]);

  // Click outside closes the dropdown
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        dispatch(closeDropdown());
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dispatch]);

  // Focus the search input when the dropdown opens
  useEffect(() => {
    if (open && searchRef.current) {
      const t = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  const selectedJobs = useMemo(
    () => jobs.filter((j) => scope.includes(j.id)),
    [jobs, scope]
  );

  const filteredJobs = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter(
      (j) =>
        (j.name || "").toLowerCase().includes(q) ||
        (j.location || "").toLowerCase().includes(q)
    );
  }, [jobs, search]);

  const allFilteredSelected =
    filteredJobs.length > 0 && filteredJobs.every((j) => scope.includes(j.id));

  const handleAddClick = () => {
    if (open) dispatch(closeDropdown());
    else dispatch(openDropdown());
  };

  const handleToggleAllVisible = () => {
    if (allFilteredSelected) {
      // Deselect only the visible ones
      const visibleIds = new Set(filteredJobs.map((j) => j.id));
      dispatch(
        selectAll(scope.filter((id) => !visibleIds.has(id)))
      );
    } else {
      const merged = Array.from(
        new Set([...scope, ...filteredJobs.map((j) => j.id)])
      );
      dispatch(selectAll(merged));
    }
  };

  return (
    <div
      ref={ref}
      data-tour="job-scope"
      className="flex-1 flex items-center gap-1 bg-vf-tint border border-slate-200 rounded-md px-2 py-1 min-h-[32px] relative"
    >
      <span className="text-[10px] font-medium text-vf-muted uppercase tracking-wider mr-1 flex-shrink-0">
        Scope:
      </span>

      {selectedJobs.length === 0 && (
        <span className="text-xs text-vf-muted">No jobs selected</span>
      )}

      <div className="flex flex-wrap items-center gap-1 min-w-0">
        {selectedJobs.map((j) => (
          <span
            key={j.id}
            className="bg-vf-tint text-vf-primary pl-2 pr-1 py-0.5 rounded text-xs font-medium inline-flex items-center gap-1.5 flex-shrink-0 max-w-[180px]"
            title={j.name}
          >
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ background: jobColor(j.id) }}
            />
            <span className="truncate">{j.name}</span>
            <button
              onClick={() => dispatch(toggleJob(j.id))}
              className="w-3.5 h-3.5 rounded hover:bg-vf-highlight flex items-center justify-center text-vf-primary leading-none flex-shrink-0"
              aria-label={`Remove ${j.name} from scope`}
            >
              ×
            </button>
          </span>
        ))}
      </div>

      <button
        onClick={handleAddClick}
        className="ml-auto text-xs text-vf-primary font-medium px-2 py-0.5 rounded border border-dashed border-vf-primary hover:bg-vf-tint flex-shrink-0 inline-flex items-center gap-1"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="leading-none">+</span>
        {scope.length === 0 ? "Select jobs" : "Add"}
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1 bg-white border border-slate-200 rounded-lg w-80 shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex justify-between items-center px-3 pt-3 pb-2">
            <span className="text-[11px] font-semibold text-vf-base uppercase tracking-wider">
              Scope your work
            </span>
            <span className="text-[11px] text-vf-muted">
              {scope.length} of {jobs.length} selected
            </span>
          </div>

          {/* Search */}
          <div className="px-3 pb-2">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => dispatch(setScopeSearch(e.target.value))}
              placeholder="Search by title or location…"
              className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:border-vf-primary focus:ring-1 focus:ring-vf-highlight"
            />
          </div>

          {/* Bulk actions */}
          <div className="flex items-center justify-between px-3 pb-2 text-xs">
            <button
              onClick={handleToggleAllVisible}
              disabled={filteredJobs.length === 0}
              className="text-vf-primary font-medium hover:underline disabled:text-vf-muted disabled:no-underline"
            >
              {allFilteredSelected ? "Deselect visible" : "Select visible"}
            </button>
            <button
              onClick={() => dispatch(clearScope())}
              disabled={scope.length === 0}
              className="text-vf-muted hover:text-vf-base disabled:opacity-50"
            >
              Clear all
            </button>
          </div>

          {/* List */}
          <div className="max-h-72 overflow-y-auto border-t border-slate-100">
            {loading && (
              <div className="px-3 py-6 text-xs text-vf-muted text-center">
                Loading jobs…
              </div>
            )}
            {!loading && filteredJobs.length === 0 && (
              <div className="px-3 py-6 text-xs text-vf-muted text-center">
                {jobs.length === 0
                  ? "No jobs found. Create one in Plan → Jobs."
                  : "No jobs match your search."}
              </div>
            )}
            {!loading &&
              filteredJobs.map((j) => {
                const checked = scope.includes(j.id);
                return (
                  <button
                    key={j.id}
                    onClick={() => dispatch(toggleJob(j.id))}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${checked ? "bg-vf-tint" : "hover:bg-vf-tint"}`}
                  >
                    <span
                      className={`w-3.5 h-3.5 rounded border flex items-center justify-center text-[10px] flex-shrink-0 ${checked ? "bg-vf-primary border-vf-primary text-white" : "border-slate-300 bg-white"}`}
                    >
                      {checked && "✓"}
                    </span>
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ background: jobColor(j.id) }}
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block text-vf-base font-medium truncate">
                        {j.name || "Untitled job"}
                      </span>
                      {j.location && (
                        <span className="block text-[10px] text-vf-muted truncate">
                          {j.location}
                        </span>
                      )}
                    </span>
                    <span className="text-[10px] text-vf-muted flex-shrink-0">
                      {j.openings || 0} open
                    </span>
                  </button>
                );
              })}
          </div>

          {/* Footer */}
          <div className="border-t border-slate-100 px-3 py-2 flex items-center justify-between bg-slate-50">
            <button
              onClick={() => dispatch(fetchJobsForScope())}
              className="text-[11px] text-vf-muted hover:text-vf-base"
            >
              ↻ Refresh
            </button>
            <button
              onClick={() => dispatch(closeDropdown())}
              className="text-[11px] font-medium text-vf-primary hover:underline"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
